import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntityOptions } from '@redux/modules/module'

import type { AgencyOrderBundleModel } from '@models/types'
import { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['agencyOrderBundles']

type HandleSortParams = {
  agencies: EntitiesState['agencies'],
  agencyOrderBundle: AgencyOrderBundleModel,
  orderBundles: EntitiesState['orderBundles'],
  sortKey: string,
}

const handleSort = (params: HandleSortParams) => {
  const {
    agencies, agencyOrderBundle, orderBundles, sortKey,
  } = params

  if (sortKey === 'order_bundle_id'){
    return orderBundles[agencyOrderBundle.order_bundle_id]?.title
  }

  return agencies[agencyOrderBundle.agency_id]?.title
}

type AgencyOrderBundleFilters = {
  agency_id?: number,
  agency_string?: string,
  hidden?: 'true' | 'false',
  order_bundle_id?: number,
  order_bundle_string?: string,
  provider_price_list_id?: number,
  publication_id?: number,
}

type Options = {
  entityKey?: string,
  filters?: AgencyOrderBundleFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
  sortKey?: string,
}

interface UseAgencyOrderBundlesPayload {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredAgencyOrderBundles: AgencyOrderBundleModel[],
  filteredAgencyOrderBundlesCount: number,
  hasFilteredAgencyOrderBundles: boolean,
  loading: boolean,
}

function useAgencyOrderBundles(options: Options = {}): UseAgencyOrderBundlesPayload {
  const {
    entityKey, filters, requestOptions, sortKey = 'agency_id',
  } = options
  const {
    agency_id: filterAgencyId,
    agency_string: filterAgencyString,
    hidden: filterHidden,
    order_bundle_id: filterOrderBundleId,
    order_bundle_string: filterOrderBundleString,
    provider_price_list_id: filterProviderPriceListId,
    publication_id: filterPublicationId,
  } = filters || {}

  const {
    updatedEntities: { agencyOrderBundles: agencyOrderBundleUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencies, agencyOrderBundles, orderBundles } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyOrderBundles = useMemo((): AgencyOrderBundleModel[] => {
    const filtered = Object.values(agencyOrderBundles).filter((agencyOrderBundle) => {
      const {
        assigned_price_list_ids = [],
        assigned_publication_ids = [],
        agency_id,
        hidden,
        order_bundle_id,
      } = agencyOrderBundle

      const { title: agencyTitle } = agencies[agency_id] || {}
      const { title: orderBundleTitle } = orderBundles[order_bundle_id] || {}

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchOrderBundleId = matchFilterNumber(order_bundle_id, filterOrderBundleId)

      const matchAgencyStringId = agency_id?.toString() === filterAgencyString
      const matchAgencyTitle = matchFilterString(agencyTitle, filterAgencyString)
      const matchAgencyString = matchAgencyStringId || matchAgencyTitle

      const matchOrderBundleStringId = order_bundle_id?.toString() === filterOrderBundleString
      const matchOrderBundleTitle = matchFilterString(orderBundleTitle, filterOrderBundleString)
      const matchOrderBundleString = matchOrderBundleStringId || matchOrderBundleTitle

      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      const matchPublicationId = filterPublicationId
        ? matchFilterArrayIncludes(assigned_publication_ids, filterPublicationId)
        : true

      const matchProviderPriceListId = filterProviderPriceListId
        ? matchFilterArrayIncludes(assigned_price_list_ids, filterProviderPriceListId)
        : true

      return matchAgencyId
        && matchAgencyString
        && matchHidden
        && matchOrderBundleId
        && matchOrderBundleString
        && matchProviderPriceListId
        && matchPublicationId
    })

    return sortArrayBy(
      filtered,
      'asc',
      agencyOrderBundle => handleSort({
        agencies, agencyOrderBundle, orderBundles, sortKey,
      }),
    )
  }, [agencyOrderBundleUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyOrderBundlesCount = filteredAgencyOrderBundles.length
  const hasFilteredAgencyOrderBundles = !!filteredAgencyOrderBundlesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyOrderBundlesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyOrderBundles } = useReduxAction(
    'agencyOrderBundles',
    'loadAgencyOrderBundles',
    {
      entityKey,
      limit,
      ...filtersWithOffset,
      ...requestOptions,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyOrderBundles,
    filteredAgencyOrderBundlesCount,
    hasFilteredAgencyOrderBundles,
    loading: !!loadingAgencyOrderBundles,
  }
}

export default useAgencyOrderBundles
